import { useEffect, useRef, useState } from 'react';
import { SIM_START, simKey, applySimKey } from '../../lib/simHand.js';

// The instructor's keyboard is usually focused on the control panel, not on
// the projector window, so the panel relays demo keys over this channel.
export const SIM_CHANNEL = 'lc-camtags-sim';

function typing(e) {
  const t = e.target;
  return t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable);
}

/**
 * The demo hands' state, driven by keys here and keys relayed from the
 * panel, plus whether the instructor pressed D to switch to the demo.
 */
export function useSimInput(enabled) {
  const [state, setState] = useState(SIM_START);
  const [demoAsked, setDemoAsked] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    const press = (key) => {
      if (key === 'd') setDemoAsked(true);
      else setState((s) => applySimKey(s, key));
    };
    const onKey = (e) => {
      if (typing(e)) return;
      const key = simKey(e);
      if (!key) return;
      e.preventDefault();
      press(key);
    };
    window.addEventListener('keydown', onKey);
    let channel = null;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(SIM_CHANNEL);
      channel.onmessage = (m) => {
        if (m.data && typeof m.data.key === 'string') press(m.data.key);
      };
    }
    return () => {
      window.removeEventListener('keydown', onKey);
      channel?.close();
    };
  }, [enabled]);

  return { sim: state, demoAsked };
}

/** On the control panel: pass demo keys to the projector while its camera slide is up. */
export function useSimRelay(active) {
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel(SIM_CHANNEL);
    const onKey = (e) => {
      if (!activeRef.current || typing(e)) return;
      const key = simKey(e);
      if (!key) return;
      e.preventDefault();
      channel.postMessage({ key });
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      channel.close();
    };
  }, []);
}
